import React, {useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef} from "react";
import {useDebounce} from "use-debounce";
import LoadingSpinner from "../ui/LoadingSpinner";
import {SearchInput} from "../search-input";

type HasID = {id: string};

export type SelectListItem<T extends HasID> = {
    item: T;
    label: React.ReactNode;
    sublabel?: React.ReactNode;
};

export type SelectListPage<T extends HasID> = {
    items: SelectListItem<T>[];
    totalPages: number;
};

type SelectListProps<T extends HasID> = {
    getItems: (search: string, page: number) => Promise<SelectListItem<T>[] | SelectListPage<T>>;
    selectedID: string | null;
    onSelect: (item: T) => void;
    header?: React.ReactNode;
    pageSize?: number;
    initialPage?: number;
    hasSublabel?: boolean;
    renderItem?: (item: T, selected: boolean) => React.ReactNode;
    onPageChange?: (page: number) => void;
};

export type SelectListHandle = {
    refresh: () => void;
};

export const SelectList = forwardRef(function SelectListInner<T extends HasID>({
    getItems,
    selectedID,
    onSelect,
    header,
    pageSize,
    initialPage,
    hasSublabel: hasSublabelProp,
    renderItem,
    onPageChange,
}: SelectListProps<T>, ref: React.Ref<SelectListHandle>) {
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 300);
    const [items, setItems] = useState<SelectListItem<T>[]>([]);
    const [page, setPage] = useState(initialPage ?? 0);
    const [totalPages, setTotalPages] = useState(1);
    const [showSpinner, setShowSpinner] = useState(false);
    const [loading, setLoading] = useState(true);
    const [skeletonVisible, setSkeletonVisible] = useState(false);
    const [hasSublabel, setHasSublabel] = useState(hasSublabelProp ?? false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const activeRef = useRef(0);

    useEffect(() => {
        searchInputRef.current?.focus();
        requestAnimationFrame(() => setSkeletonVisible(true));
    }, []);

    const fetchPage = useCallback((search: string, p: number) => {
        const id = ++activeRef.current;
        setLoading(true);
        const timer = setTimeout(() => {
            if (id === activeRef.current) setShowSpinner(true);
        }, 500);
        getItems(search, p).then(result => {
            if (id !== activeRef.current) return;
            if (Array.isArray(result)) {
                setItems(result);
                setTotalPages(1);
                if (result.length > 0) setHasSublabel(result.some(({sublabel}) => sublabel != null));
            } else {
                setItems(result.items);
                setTotalPages(result.totalPages);
                if (result.items.length > 0) setHasSublabel(result.items.some(({sublabel}) => sublabel != null));
            }
        }).finally(() => {
            clearTimeout(timer);
            if (id === activeRef.current) {
                setShowSpinner(false);
                setLoading(false);
            }
        });
    }, [getItems]);

    useEffect(() => {
        setPage(0);
        fetchPage(debouncedSearch, 0);
    }, [debouncedSearch, fetchPage]);

    const handlePageChange = useCallback((p: number) => {
        setPage(p);
        fetchPage(debouncedSearch, p);
        onPageChange?.(p);
    }, [debouncedSearch, fetchPage, onPageChange]);

    useImperativeHandle(ref, () => ({
        refresh: () => fetchPage(debouncedSearch, page),
    }), [fetchPage, debouncedSearch, page]);

    const handleSelect = useCallback((item: T) => {
        onSelect(item);
    }, [onSelect]);

    return (
        <div className="flex flex-col h-fit bg-white rounded-lg shadow-md overflow-hidden">
            {header && (
                <div className="shrink-0 flex flex-col">
                    {header}
                </div>
            )}
            <div className="p-3 border-b border-light-light-grey shrink-0">
                <SearchInput ref={searchInputRef} initialValue={search} onChange={setSearch}/>
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading && pageSize && Array.from({length: pageSize}).map((_, i) => (
                    <div key={`loading-${i}`} className="px-4 py-3 border-b border-light-light-grey flex flex-col gap-0.5 animate-pulse transition-opacity duration-500" style={{opacity: skeletonVisible ? 1 : 0}}>
                        <div className="h-5 rounded bg-light-light-grey opacity-40"/>
                        {hasSublabel && <div className="h-4 w-2/3 rounded bg-light-light-grey opacity-40"/>}
                    </div>
                ))}
                {loading && !pageSize && showSpinner && (
                    <div className="flex justify-center py-6">
                        <LoadingSpinner/>
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <div className="text-center text-sm text-light-grey py-8">No results found</div>
                )}
                {!(loading && pageSize) && items.map(({item, label, sublabel}) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => handleSelect(item)}
                        className={`w-full text-left px-4 py-3 border-b border-light-light-grey last:border-none transition-colors flex flex-col gap-0.5 ${
                            selectedID === item.id
                                ? "bg-blue text-white"
                                : "hover:bg-light-light-grey"
                        }`}
                    >
                        {renderItem ? renderItem(item, selectedID === item.id) : (
                            <>
                                <span className="text-sm font-medium min-w-0 w-full">{label}</span>
                                {sublabel && (
                                    <span className={`text-xs ${selectedID === item.id ? "text-white opacity-75" : "text-light-grey"}`}>
                                        {sublabel}
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                ))}
                {pageSize && !loading && items.length > 0 && items.length < pageSize && Array.from({length: pageSize - items.length}).map((_, i) => (
                    <div key={`skeleton-${i}`} className="px-4 py-3 border-b border-transparent flex flex-col gap-0.5">
                        <div className="h-5"/>
                        {hasSublabel && <div className="h-4"/>}
                    </div>
                ))}
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-light-light-grey shrink-0">
                    <button
                        type="button"
                        className="text-sm text-blue disabled:text-light-grey"
                        disabled={page === 0}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        ← Prev
                    </button>
                    <span className="text-xs text-light-grey">{page + 1} / {totalPages}</span>
                    <button
                        type="button"
                        className="text-sm text-blue disabled:text-light-grey"
                        disabled={page >= totalPages - 1}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
}) as <T extends HasID>(props: SelectListProps<T> & {ref?: React.Ref<SelectListHandle>}) => React.ReactElement;
